/**
 * Local-First Scenario Storage Service
 *
 * Stores scenarios, bookmarks, and searches in localStorage (web) or SQLite (mobile WebViewGold)
 * No authentication required - everything is stored locally
 * Optional publishing to community when backend is available
 */

import { v4 as uuidv4 } from 'uuid';

export interface LocalScenarioProcedure {
  code: string;
  codeType: string;
  description: string;
  phase: string;
  quantity: number;
  estimatedCost: number;
  dayNumber?: number;
  setting?: 'inpatient' | 'outpatient' | 'unspecified';
  frequency?: 'one-time' | 'monthly' | 'quarterly' | 'semi-annual' | 'annual';
}

export interface LocalScenario {
  id: string;
  uuid: string;
  slug: string;
  name: string;
  description: string;
  duration: string;
  totalProcedures: number;
  estimatedCost: number;
  procedures: LocalScenarioProcedure[];
  phases: string[];
  tags: string[];
  severityLevel?: number; // 1-10 scale: 1=simple/routine, 10=worst-case/highly complicated

  // Local metadata
  isLocal: true;
  isDraft: boolean;
  createdAt: string;
  updatedAt: string;

  // Sharing & visibility
  visibility: 'public' | 'unlisted';
  shareToken: string;
  source: 'manual' | 'ai_generated' | 'template';

  // Family planner
  familyMemberId?: string;
  plannedDate?: string; // ISO date string — when scenario is planned to start

  // Hospital preference (from optimizer)
  preferredHospitalId?: string;
  preferredHospitalName?: string;
  hospitalEstimatedCost?: number;

  // Care Plan (DB-persisted optimization)
  carePlanId?: string;
  carePlanShareToken?: string;

  // Optional publishing metadata
  isPublished?: boolean;
  publishedAt?: string;
  serverId?: string; // ID from server if published
}

export interface UserPreferences {
  userId: string; // Anonymous local ID
  bookmarkedScenarioIds: string[];
  recentSearches: string[];
  recentScenarioViews: string[];
}

class ScenarioStorageService {
  private readonly STORAGE_KEYS = {
    USER_SCENARIOS: 'luigis_user_scenarios',
    BOOKMARKS: 'luigis_bookmarked_scenarios',
    RECENT_SEARCHES: 'luigis_recent_searches',
    RECENT_VIEWS: 'luigis_recent_scenario_views',
    USER_ID: 'luigis_anonymous_user_id',
  };

  private readonly MAX_RECENT_ITEMS = 20;

  /**
   * Get or create anonymous user ID
   */
  getUserId(): string {
    let userId = localStorage.getItem(this.STORAGE_KEYS.USER_ID);
    if (!userId) {
      userId = uuidv4();
      localStorage.setItem(this.STORAGE_KEYS.USER_ID, userId);
    }
    return userId;
  }

  /**
   * Get all user-created scenarios
   */
  getUserScenarios(): LocalScenario[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.USER_SCENARIOS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading user scenarios:', error);
      return [];
    }
  }

  /**
   * Save a scenario locally
   */
  saveScenario(scenario: Omit<LocalScenario, 'id' | 'uuid' | 'slug' | 'isLocal' | 'createdAt' | 'updatedAt'>): LocalScenario {
    const scenarios = this.getUserScenarios();

    const newScenario: LocalScenario = {
      ...scenario,
      id: uuidv4(),
      uuid: uuidv4(),
      slug: this.generateSlug(scenario.name),
      isLocal: true,
      visibility: scenario.visibility || 'unlisted',
      shareToken: scenario.shareToken || this.generateShareToken(),
      source: scenario.source || 'manual',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    scenarios.push(newScenario);
    localStorage.setItem(this.STORAGE_KEYS.USER_SCENARIOS, JSON.stringify(scenarios));

    return newScenario;
  }

  /**
   * Update an existing scenario
   */
  updateScenario(scenarioId: string, updates: Partial<LocalScenario>): LocalScenario | null {
    const scenarios = this.getUserScenarios();
    const index = scenarios.findIndex(s => s.id === scenarioId);

    if (index === -1) return null;

    scenarios[index] = {
      ...scenarios[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(this.STORAGE_KEYS.USER_SCENARIOS, JSON.stringify(scenarios));
    return scenarios[index];
  }

  /**
   * Delete a scenario
   */
  deleteScenario(scenarioId: string): boolean {
    const scenarios = this.getUserScenarios();
    const filtered = scenarios.filter(s => s.id !== scenarioId);

    if (filtered.length === scenarios.length) return false;

    localStorage.setItem(this.STORAGE_KEYS.USER_SCENARIOS, JSON.stringify(filtered));
    return true;
  }

  /**
   * Assign a scenario to a family member (or unassign with null)
   */
  assignToFamilyMember(scenarioId: string, familyMemberId: string | null): LocalScenario | null {
    return this.updateScenario(scenarioId, { familyMemberId: familyMemberId ?? undefined });
  }

  /**
   * Get bookmarked scenario IDs
   */
  getBookmarks(): string[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.BOOKMARKS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading bookmarks:', error);
      return [];
    }
  }

  /**
   * Toggle bookmark for a scenario
   */
  toggleBookmark(scenarioId: string): boolean {
    const bookmarks = this.getBookmarks();
    const index = bookmarks.indexOf(scenarioId);

    if (index === -1) {
      bookmarks.push(scenarioId);
    } else {
      bookmarks.splice(index, 1);
    }

    localStorage.setItem(this.STORAGE_KEYS.BOOKMARKS, JSON.stringify(bookmarks));
    // Notify listeners (e.g. the /followed page, other open cards) so the
    // followed-scenarios list and stars stay in sync without a reload.
    try { window.dispatchEvent(new Event('ahp-scenario-bookmarks-changed')); } catch { /* ignore */ }
    return index === -1; // Return true if bookmarked, false if unbookmarked
  }

  /**
   * Check if scenario is bookmarked
   */
  isBookmarked(scenarioId: string): boolean {
    return this.getBookmarks().includes(scenarioId);
  }

  /**
   * Add to recent searches
   */
  addRecentSearch(query: string): void {
    if (!query || query.trim().length === 0) return;

    const searches = this.getRecentSearches();

    // Remove if already exists
    const filtered = searches.filter(s => s.toLowerCase() !== query.toLowerCase());

    // Add to front
    filtered.unshift(query.trim());

    // Limit to max items
    const limited = filtered.slice(0, this.MAX_RECENT_ITEMS);

    localStorage.setItem(this.STORAGE_KEYS.RECENT_SEARCHES, JSON.stringify(limited));
  }

  /**
   * Get recent searches
   */
  getRecentSearches(): string[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.RECENT_SEARCHES);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading recent searches:', error);
      return [];
    }
  }

  /**
   * Clear recent searches
   */
  clearRecentSearches(): void {
    localStorage.setItem(this.STORAGE_KEYS.RECENT_SEARCHES, JSON.stringify([]));
  }

  /**
   * Add to recent scenario views
   */
  addRecentView(scenarioId: string): void {
    const views = this.getRecentViews();

    // Remove if already exists
    const filtered = views.filter(id => id !== scenarioId);

    // Add to front
    filtered.unshift(scenarioId);

    // Limit to max items
    const limited = filtered.slice(0, this.MAX_RECENT_ITEMS);

    localStorage.setItem(this.STORAGE_KEYS.RECENT_VIEWS, JSON.stringify(limited));
  }

  /**
   * Get recent scenario views
   */
  getRecentViews(): string[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.RECENT_VIEWS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading recent views:', error);
      return [];
    }
  }

  /**
   * Attempt to publish scenario to community
   * Returns true if successful, false if backend unavailable
   */
  async publishScenario(scenarioId: string): Promise<{ success: boolean; message: string; serverId?: string }> {
    const scenario = this.getUserScenarios().find(s => s.id === scenarioId);

    if (!scenario) {
      return { success: false, message: 'Scenario not found' };
    }

    if (scenario.isPublished) {
      return { success: false, message: 'Scenario already published' };
    }

    try {
      // TODO: Replace with actual API endpoint when backend is ready
      const response = await fetch('/api/scenarios/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenario,
          userId: this.getUserId(),
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Update local scenario with published status
        this.updateScenario(scenarioId, {
          isPublished: true,
          publishedAt: new Date().toISOString(),
          serverId: data.id,
        });

        return {
          success: true,
          message: 'Scenario published successfully!',
          serverId: data.id,
        };
      } else {
        return {
          success: false,
          message: 'Server error. Your scenario is saved locally. Try publishing again later.',
        };
      }
    } catch (error) {
      // Backend offline or network error - gracefully fail
      return {
        success: false,
        message: 'Publishing service temporarily unavailable. Your scenario is saved locally and you can try publishing again later.',
      };
    }
  }

  /**
   * Export all user data (for backup/migration)
   */
  exportUserData(): string {
    const data = {
      userId: this.getUserId(),
      scenarios: this.getUserScenarios(),
      bookmarks: this.getBookmarks(),
      recentSearches: this.getRecentSearches(),
      recentViews: this.getRecentViews(),
      exportedAt: new Date().toISOString(),
    };

    return JSON.stringify(data, null, 2);
  }

  /**
   * Import user data (from backup)
   */
  importUserData(jsonData: string): { success: boolean; message: string } {
    try {
      const data = JSON.parse(jsonData);

      if (data.scenarios) {
        localStorage.setItem(this.STORAGE_KEYS.USER_SCENARIOS, JSON.stringify(data.scenarios));
      }
      if (data.bookmarks) {
        localStorage.setItem(this.STORAGE_KEYS.BOOKMARKS, JSON.stringify(data.bookmarks));
      }
      if (data.recentSearches) {
        localStorage.setItem(this.STORAGE_KEYS.RECENT_SEARCHES, JSON.stringify(data.recentSearches));
      }
      if (data.recentViews) {
        localStorage.setItem(this.STORAGE_KEYS.RECENT_VIEWS, JSON.stringify(data.recentViews));
      }

      return { success: true, message: 'Data imported successfully!' };
    } catch (error) {
      return { success: false, message: 'Invalid import data format' };
    }
  }

  /**
   * Generate URL-friendly slug from scenario name
   */
  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Generate a random share token (24-char hex)
   */
  private generateShareToken(): string {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get a scenario's shareable URL
   */
  getShareUrl(scenario: LocalScenario): string {
    const base = `${window.location.origin}/scenarios/${scenario.uuid}/${scenario.slug}`;
    if (scenario.visibility === 'unlisted') {
      return `${base}?t=${scenario.shareToken}`;
    }
    return base;
  }

  // ── Helpful votes ──────────────────────────────────────────────

  getHelpfulVotes(): string[] {
    try {
      const data = localStorage.getItem('luigis_helpful_votes');
      return data ? JSON.parse(data) : [];
    } catch { return []; }
  }

  toggleHelpful(scenarioId: string): boolean {
    const votes = new Set(this.getHelpfulVotes());
    const wasVoted = votes.has(scenarioId);
    if (wasVoted) votes.delete(scenarioId);
    else votes.add(scenarioId);
    localStorage.setItem('luigis_helpful_votes', JSON.stringify([...votes]));
    return !wasVoted;
  }

  // ── Follow / save ────────────────────────────────────────────

  getFollowedScenarios(): string[] {
    try {
      const data = localStorage.getItem('luigis_followed_scenarios');
      return data ? JSON.parse(data) : [];
    } catch { return []; }
  }

  toggleFollow(scenarioId: string): boolean {
    const follows = new Set(this.getFollowedScenarios());
    const wasFollowed = follows.has(scenarioId);
    if (wasFollowed) follows.delete(scenarioId);
    else follows.add(scenarioId);
    localStorage.setItem('luigis_followed_scenarios', JSON.stringify([...follows]));
    return !wasFollowed;
  }

  // ── Account nudge ────────────────────────────────────────────

  shouldShowAccountNudge(): boolean {
    const votes = this.getHelpfulVotes().length;
    const follows = this.getFollowedScenarios().length;
    return (votes + follows) >= 3;
  }

  /**
   * Clear all local data (for testing or user request)
   */
  clearAllData(): void {
    Object.values(this.STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    localStorage.removeItem('luigis_helpful_votes');
    localStorage.removeItem('luigis_followed_scenarios');
  }
}

// Export singleton instance
export const scenarioStorage = new ScenarioStorageService();
